import { createClient } from '@supabase/supabase-js';

// Create a single supabase client for interacting with your database
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Clear any stale auth data before initializing
const clearStaleAuthData = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.startsWith('supabase'))) {
        const item = localStorage.getItem(key);
        if (item) {
          try {
            const parsed = JSON.parse(item);
            if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
              keysToRemove.push(key);
            }
          } catch {
            // Corrupted JSON, remove it
            keysToRemove.push(key);
          }
        }
      }
    }
    
    if (keysToRemove.length > 0) {
      console.log('Clearing stale/corrupted auth data from localStorage:', keysToRemove);
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  } catch (error) {
    console.error('Error clearing stale auth data:', error);
  }
};

// Clear stale data on initialization
clearStaleAuthData();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key: string) => {
        const item = localStorage.getItem(key);
        // Add timestamp check for extra safety
        if (item && key.includes('auth')) {
          try {
            const parsed = JSON.parse(item);
            if (parsed.expires_at && new Date(parsed.expires_at * 1000) < new Date()) {
              localStorage.removeItem(key);
              return null;
            }
          } catch {}
        }
        return item;
      },
      setItem: (key: string, value: string) => {
        localStorage.setItem(key, value);
      },
      removeItem: (key: string) => {
        localStorage.removeItem(key);
      }
    }
  }
});

// Database types
export interface Profile {
  id: string;
  full_name: string | null;
  company_name: string | null;
  avatar_url: string | null;
  account_type: 'user' | 'team' | 'super_admin';
  license?: 'free' | 'commercial' | 'template_pack' | 'professional' | 'agency';
  credits_balance?: number;
  role: 'user' | 'admin' | 'super_admin';
  monthly_mockup_limit: number;
  mockups_created_this_month: number;
  subscription_status: string;
  subscription_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BrandSettings {
  id: string;
  user_id: string;
  brand_name: string | null;
  tagline: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface Asset {
  id: string;
  user_id: string;
  filename: string;
  original_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  created_at: string;
  updated_at: string;
}

export interface Template {
  id: string;
  name: string;
  category: string;
  description: string | null;
  preview_url: string | null;
  is_premium: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface Mockup {
  id: string;
  user_id: string;
  template_id: string | null;
  name: string;
  primary_image_id: string | null;
  secondary_image_id: string | null;
  mockup_url: string;
  settings: any;
  downloads: number;
  created_at: string;
  updated_at: string;
  // Relations
  template?: Template;
  primary_image?: Asset;
  secondary_image?: Asset;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_updates: boolean;
  marketing_emails: boolean;
  product_news: boolean;
  created_at: string;
  updated_at: string;
}
